/**
 * "생활광고·모임 관리" — moderation queue for city_classifieds.
 * Backend: GET/PATCH /api/admin/city-classifieds (functions/api/admin/city-classifieds/*),
 * admin-only via requireAdminKey.
 *
 * "모임" (meetups) are not a separate table - they live in city_classifieds
 * under category ids like "events"/"event" (shared/schema.ts's
 * cityClassifiedCategories; functions/api/meetups/* is just a redirect alias
 * to /api/city-classifieds). The category filter below doubles as the
 * 모임-only view.
 */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Ban, EyeOff, Megaphone, RotateCcw, Trash2 } from "lucide-react";
import { apiGet, apiPatch } from "./api";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AdminPageLayout from "./AdminPageLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type ClassifiedStatus = "published" | "hidden" | "cancelled" | "deleted";

type Classified = {
  id: number;
  title: string;
  body: string | null;
  category: string | null;
  state: string | null;
  city: string | null;
  suburb: string | null;
  status: ClassifiedStatus;
  views: number;
  isPremium: number;
  whenAt: string | null;
  capacity: number | null;
  hostId: number;
  hostDisplayName: string | null;
  hostEmail: string | null;
  createdAt: string;
  updatedAt: string | null;
};

type ClassifiedsResponse = { ok: true; items: Classified[]; total: number; limit: number; offset: number };

const statusOptions: { value: ClassifiedStatus | "all"; label: string }[] = [
  { value: "all", label: "전체" },
  { value: "published", label: "게시중" },
  { value: "hidden", label: "숨김" },
  { value: "cancelled", label: "취소됨" },
  { value: "deleted", label: "삭제됨" },
];
const statusLabel: Record<string, string> = Object.fromEntries(statusOptions.map((o) => [o.value, o.label]));
const MEETUP_CATEGORIES = new Set(["events", "event"]);

export default function CityClassifiedsModeration() {
  const { toast } = useToast();
  const [status, setStatus] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<"all" | "meetups" | "other">("all");
  const [q, setQ] = useState("");

  const params = new URLSearchParams();
  if (status !== "all") params.set("status", status);
  if (q.trim()) params.set("q", q.trim());
  const queryString = params.toString();

  const list = useQuery({
    queryKey: ["admin-city-classifieds", status, q],
    queryFn: () => apiGet<ClassifiedsResponse>(`/api/admin/city-classifieds${queryString ? `?${queryString}` : ""}`),
  });

  const patch = useMutation({
    mutationFn: ({ id, status: newStatus }: { id: number; status: ClassifiedStatus }) =>
      apiPatch(`/api/admin/city-classifieds/${id}`, { status: newStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-city-classifieds"] });
      toast({ title: "상태를 변경했습니다" });
    },
    onError: (error: Error) => toast({ title: "변경 실패", description: error.message, variant: "destructive" }),
  });

  const allItems = list.data?.items || [];
  const items = allItems.filter((item) => {
    if (categoryFilter === "all") return true;
    const isMeetup = MEETUP_CATEGORIES.has(item.category || "");
    return categoryFilter === "meetups" ? isMeetup : !isMeetup;
  });

  return (
    <AdminPageLayout
      title="생활광고·모임 관리"
      description="생활광고(서비스·레슨·구인구직 등)와 모임/이벤트 게시글을 확인하고 숨김·취소·삭제 처리를 합니다."
      icon={<Megaphone className="w-8 h-8" />}
      loading={list.isLoading}
      error={list.error ? (list.error as Error).message : null}
    >
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap gap-3">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                {statusOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as typeof categoryFilter)}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">전체 (광고+모임)</SelectItem>
                <SelectItem value="meetups">모임/이벤트만</SelectItem>
                <SelectItem value="other">생활광고만</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="제목·내용 검색"
              className="w-64"
            />
            <div className="ml-auto text-sm text-muted-foreground self-center">
              {list.data ? `총 ${list.data.total}건 (필터 후 ${items.length}건)` : ""}
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>제목</TableHead>
                  <TableHead>등록자</TableHead>
                  <TableHead>구분/지역</TableHead>
                  <TableHead>조회</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead className="text-right">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const isMeetup = MEETUP_CATEGORIES.has(item.category || "");
                  return (
                    <TableRow key={item.id}>
                      <TableCell className="max-w-xs">
                        <div className="truncate font-medium" title={item.title}>{item.title}</div>
                        <div className="truncate text-xs text-muted-foreground" title={item.body || ""}>
                          {item.body}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(item.createdAt).toLocaleString("ko-KR")}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.hostDisplayName || item.hostEmail || `#${item.hostId}`}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge variant={isMeetup ? "default" : "outline"} className="text-xs">
                            {isMeetup ? "모임" : item.category || "기타"}
                          </Badge>
                          {item.isPremium === 1 && (
                            <Badge variant="secondary" className="text-xs">프리미엄</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {[item.state, item.city, item.suburb].filter(Boolean).join(" · ") || "-"}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.views}</TableCell>
                      <TableCell>
                        <Badge variant={item.status === "published" ? "default" : item.status === "hidden" ? "secondary" : "outline"}>
                          {statusLabel[item.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap justify-end gap-1">
                          {item.status !== "published" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => patch.mutate({ id: item.id, status: "published" })}
                            >
                              <RotateCcw className="mr-1 h-3 w-3" />게시 복구
                            </Button>
                          )}
                          {item.status !== "hidden" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => patch.mutate({ id: item.id, status: "hidden" })}
                            >
                              <EyeOff className="mr-1 h-3 w-3" />숨김
                            </Button>
                          )}
                          {isMeetup && item.status !== "cancelled" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => patch.mutate({ id: item.id, status: "cancelled" })}
                            >
                              <Ban className="mr-1 h-3 w-3" />모임 취소
                            </Button>
                          )}
                          {item.status !== "deleted" && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => patch.mutate({ id: item.id, status: "deleted" })}
                            >
                              <Trash2 className="mr-1 h-3 w-3" />삭제
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!list.isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                      해당 조건의 게시글이 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}
